// Libraries
import { setYear, endOfYear, startOfYear } from 'date-fns';
import { DomainPropType } from 'victory';
// Components
import {
  LineType,
  Point,
  PointArea,
  VictoryArea,
  VictoryLine,
  VictoryLines,
  VictoryPointScatters,
} from '@organisms/index';
// Utils
import { findRangeKey, stringDateToTime } from '@helpers/index';
import text from '@translations/en.json';
import { transformLinesIntoRatedLines } from '@utils/index';
// Types
import { CiiRatingBoundaries, GraphicsData } from '@schemas/services';

/**
 * This function takes CII Data and converts it into a format that can be used with VictoryChart components.
 *
 * @function
 *
 * @param {GraphicsData} graphicsData - The CII Data containing information about simulated and historical CII scores.
 * @param {CiiRatingBoundaries} ciiRatingBoundaries - The rating boundaries for the Critical Information
 * @param {number} operationalYear - The year of the information
 *
 * @returns An object containing transformed data for the Victory charts including areas, lines, scatter points, domain, tick values, etc.
 * If an error occurs during transformation, an error message is returned instead.
 */
export const graphCIIDataToVictoryData = (
  graphicsData?: GraphicsData,
  ciiRatingBoundaries?: CiiRatingBoundaries,
  operationalYear?: number,
  requiredCiiScore = 0
) => {
  if (graphicsData && ciiRatingBoundaries && operationalYear) {
    try {
      const { min, max } = Math;
      const { yearToDateCIIScores, simulatedCIIScores, bulkUsageEndIndex } = graphicsData;
      const xStart = startOfYear(setYear(new Date(), operationalYear)).getTime();
      const xLimit = endOfYear(setYear(new Date(), operationalYear)).getTime();
      const tooltipBlacklist = [/^(1|2)-[A-E].*|^(up|low|[A-E]|requiredCiiScore).*/];
      const yearToDateLastCIIScore = yearToDateCIIScores[yearToDateCIIScores.length - 1];
      const yValue = yearToDateLastCIIScore?.cii ?? 0;
      const xValue = stringDateToTime(yearToDateLastCIIScore?.time) ?? new Date().getTime();
      const ranting = findRangeKey(yValue, ciiRatingBoundaries);
      const starts = Object.entries(ciiRatingBoundaries).map(([_, [start]]) => start);
      const tickValues = [...starts];
      const xTickValues = [
        new Date(operationalYear, 0, 1).getTime(), // January
        new Date(operationalYear, 6, 1).getTime(), // July
        new Date(operationalYear, 11, 31).getTime(), // December
      ];
      const bulkFirstHalf = simulatedCIIScores.slice(0, bulkUsageEndIndex);
      const bulkSecondHalf = simulatedCIIScores.slice(bulkUsageEndIndex);

      const victoryAreas = Object.entries(ciiRatingBoundaries).map(([key, [rangeStart, rangeEnd]]) => {
        const data = bulkFirstHalf.reduce<PointArea[]>((acc, { time, upperCII, lowerCII }) => {
          if (upperCII > rangeStart && lowerCII < rangeEnd) {
            acc.push({
              x: stringDateToTime(time),
              y0: min(upperCII, rangeEnd),
              y: max(lowerCII, rangeStart),
            });
          }

          return acc;
        }, []);

        return {
          rate: key, // Replace 'RatingKeys' with the actual type for the 'rate' property
          key: `1-${key}`,
          data,
          styleKey: key,
        } as VictoryArea;
      });

      const victoryAreas2 = Object.entries(ciiRatingBoundaries).map(([key, [rangeStart, rangeEnd]]) => {
        const data = bulkSecondHalf.reduce<PointArea[]>((acc, { time, upperCII, lowerCII }) => {
          if (upperCII > rangeStart && lowerCII < rangeEnd) {
            acc.push({
              x: stringDateToTime(time),
              y0: min(upperCII, rangeEnd),
              y: max(lowerCII, rangeStart),
            });
          }

          return acc;
        }, []);

        return {
          rate: key, // Replace 'RatingKeys' with the actual type for the 'rate' property
          key: `2-${key}`,
          data,
          styleKey: key,
        } as VictoryArea;
      });
      victoryAreas.push(...victoryAreas2);

      const formattedData = yearToDateCIIScores.map(({ cii, time }) => ({
        y: cii,
        x: stringDateToTime(time),
      }));

      const [mid, up, low] = simulatedCIIScores.reduce(
        ([mid, up, low]: [Point[], Point[], Point[]], { cii, time, upperCII, lowerCII }) => {
          const timestamp = stringDateToTime(time);

          mid.push({ x: timestamp, y: cii });
          up.push({ x: timestamp, y: upperCII });
          low.push({ x: timestamp, y: lowerCII });
          return [mid, up, low];
        },
        [[], [], []]
      );

      const lines = transformLinesIntoRatedLines(formattedData, ciiRatingBoundaries, text.cii_current, 'cii');
      const midLines = transformLinesIntoRatedLines(
        mid,
        ciiRatingBoundaries,
        text.cii_forecasted,
        'mid',
        LineType.Dashed
      );
      const upLines = transformLinesIntoRatedLines(up, ciiRatingBoundaries, text.cii_forecasted, 'up', LineType.Dashed);
      const lowLines = transformLinesIntoRatedLines(
        low,
        ciiRatingBoundaries,
        text.cii_forecasted,
        'low',
        LineType.Dashed
      );
      const requiredCIILine: VictoryLine = {
        data: [
          {
            y: requiredCiiScore,
            x: xStart,
          },
          {
            y: requiredCiiScore,
            x: xLimit,
          },
        ],
        styleKey: 'blackDotted',
        id: 'requiredCiiScore',
      };

      const victoryLines: VictoryLines = [...lines, ...midLines, ...upLines, ...lowLines, requiredCIILine];

      const victoryScatter: VictoryPointScatters = [
        {
          data: [
            {
              x: xValue,
              y: yValue,
            },
          ],
          styleKey: ranting,
          id: ranting,
          title: text.cii,
        },
      ];

      const maxValue = [...up, ...formattedData].reduce((max, { y }) => (Number(y) > max ? Number(y) : max), 0);
      const maxDomainValue = min(maxValue + 0.5, ciiRatingBoundaries.E[1]);
      const minValue = [...low, ...formattedData].reduce(
        (max, { y }) => (Number(y) < max ? Number(y) : max),
        maxDomainValue
      );
      const minDomainValue = max(minValue - 0.5, ciiRatingBoundaries.A[0]);
      const domain: DomainPropType = {
        y: [minDomainValue, maxDomainValue],
      };
      return {
        victoryAreas,
        victoryLines,
        victoryScatter,
        domain,
        tickValues,
        xLimit,
        tooltipBlacklist,
        xTickValues,
      };
    } catch (error) {
      // TODO: Handle error in a better way
      return {
        error: text.something_went_wrong,
      };
    }
  }
  return {
    error: text.no_data_available,
  };
};
